import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumAssemblyIngredientSource } from "./enums/enumAssemblyIngredientSource.generated";
import { EnumAssemblyOutputTarget } from "./enums/enumAssemblyOutputTarget.generated";
import { EnumAssemblyStatus } from "./enums/enumAssemblyStatus.generated";
import { EnumAssemblyType } from "./enums/enumAssemblyType.generated";
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class AssemblyOutput extends ModelObject<AssemblyOutput> {

    public quantity: number = 0;
    public stockItemId: Guid = Guid.createEmpty();
    public unitOfMeasureId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.quantity = jsonSerializedObject.quantity;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.quantity = this.quantity;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "AssemblyOutput.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "AssemblyOutput.UnitOfMeasureId cannot be empty"},
    ];
}


export class AssemblyHeaderReadOnly extends ModelObject<AssemblyHeaderReadOnly> {

    private _startedAt: Instant | null = null;
    /**
    * Timestamp at which the assembly was started.
    */
    public get startedAt(): Instant | null { return this._startedAt }
    public set startedAt(value: Instant | null) {
      this._startedAt = value === undefined ? null : value;
    }
    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the assembly was completed.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    private _expectedCompletionDateAtStart: LocalDate | null = null;
    /**
    * Expected Completion Date At Start
    */
    public get expectedCompletionDateAtStart(): LocalDate | null { return this._expectedCompletionDateAtStart }
    public set expectedCompletionDateAtStart(value: LocalDate | null) {
      this._expectedCompletionDateAtStart = value === undefined ? null : value;
    }
    /**
    * Cost of ingredients already used in assembly. (Will be 0 if not yet started)
    */
    public costSoFar: number = 0;
    private _costExpectedAtStart: number | null = null;
    /**
    * The cost which was expected as captured when the assembly was started.
    */
    public get costExpectedAtStart(): number | null { return this._costExpectedAtStart }
    public set costExpectedAtStart(value: number | null) {
      this._costExpectedAtStart = value === undefined ? null : value;
    }
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the assembly was cancelled.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    /**
    * True if this assembly has been previously completed, and then un-completed.
    */
    public hasBeenPreviouslyCompleted: boolean = false;
    /**
    * Total Litres of all ingredients already used in assembly. (Will be 0 if not yet started)
    */
    public totalIngredientUsedLitres: number = 0;
    private _startedBy: Guid | null = null;
    /**
    * User who has started the assembly.
    */
    public get startedBy(): Guid | null { return this._startedBy }
    public set startedBy(value: Guid | null) {
      this._startedBy = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.startedAt = jsonSerializedObject.startedAt == null ? null : Instant.parse(jsonSerializedObject.startedAt);
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.expectedCompletionDateAtStart = jsonSerializedObject.expectedCompletionDateAtStart == null ? null : LocalDate.parse(jsonSerializedObject.expectedCompletionDateAtStart);
          this.costSoFar = jsonSerializedObject.costSoFar;
          this.costExpectedAtStart = jsonSerializedObject.costExpectedAtStart == null ? null : jsonSerializedObject.costExpectedAtStart;
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.hasBeenPreviouslyCompleted = jsonSerializedObject.hasBeenPreviouslyCompleted;
          this.totalIngredientUsedLitres = jsonSerializedObject.totalIngredientUsedLitres;
          this.startedBy = jsonSerializedObject.startedBy == null ? null : Guid.parseOrEmpty(jsonSerializedObject.startedBy);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.startedAt = this.startedAt == null ? null : this.startedAt.toString();
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.expectedCompletionDateAtStart = this.expectedCompletionDateAtStart == null ? null : this.expectedCompletionDateAtStart.toString();
        toRet.costSoFar = this.costSoFar;
        toRet.costExpectedAtStart = this.costExpectedAtStart == null ? null : this.costExpectedAtStart;
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.hasBeenPreviouslyCompleted = this.hasBeenPreviouslyCompleted;
        toRet.totalIngredientUsedLitres = this.totalIngredientUsedLitres;
        toRet.startedBy = this.startedBy == null ? null : this.startedBy.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "startedBy", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyHeaderReadOnly.StartedBy cannot be empty"},
    ];
}


export class AssemblyHeaderCalculated extends ModelObject<AssemblyHeaderCalculated> {

    public status: EnumAssemblyStatus = EnumAssemblyStatus.open;
    private _outputExpectedAtStart: AssemblyOutput | null = null;
    public get outputExpectedAtStart(): AssemblyOutput | null { return this._outputExpectedAtStart }
    public set outputExpectedAtStart(value: AssemblyOutput | null) {
      this._outputExpectedAtStart = value === undefined ? null : value;
    }
    private _outputProduced: AssemblyOutput | null = null;
    public get outputProduced(): AssemblyOutput | null { return this._outputProduced }
    public set outputProduced(value: AssemblyOutput | null) {
      this._outputProduced = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.status = EnumAssemblyStatus.getByValue(jsonSerializedObject.status, this.status);
          this.outputExpectedAtStart = jsonSerializedObject.outputExpectedAtStart == null ? null : new AssemblyOutput(jsonSerializedObject.outputExpectedAtStart);
          this.outputProduced = jsonSerializedObject.outputProduced == null ? null : new AssemblyOutput(jsonSerializedObject.outputProduced);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.status = this.status.value;
        toRet.outputExpectedAtStart = this.outputExpectedAtStart == null ? null : this.outputExpectedAtStart?.toJsonSerializedObject() ?? null;
        toRet.outputProduced = this.outputProduced == null ? null : this.outputProduced?.toJsonSerializedObject() ?? null;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "outputExpectedAtStart", rule: (v: any) => (v === null) || v.validate()},
            {field: "outputProduced", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class AssemblyHeader extends ModelObject<AssemblyHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Assembly Type. : 0 = MaterialProduction, 1 = ProductPackaging, 2 = Maintenance
    */
    public assemblyType: EnumAssemblyType = EnumAssemblyType.materialProduction;
    private _bestBefore: LocalDate | null = null;
    /**
    * Best Before Date
    */
    public get bestBefore(): LocalDate | null { return this._bestBefore }
    public set bestBefore(value: LocalDate | null) {
      this._bestBefore = value === undefined ? null : value;
    }
    /**
    * Current Expected Completion Date
    */
    public currentExpectedCompletionDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Name. Defaults to using sequence numbering.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    private _recipeId: Guid | null = null;
    /**
    * Recipe from which this assembly was initially created (if any). Does not drive any functionality, just for future reference really.
    */
    public get recipeId(): Guid | null { return this._recipeId }
    public set recipeId(value: Guid | null) {
      this._recipeId = value === undefined ? null : value;
    }
    /**
    * Stock Location Id
    */
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * Urn. Will be either allocated at begin assembly, or will be provided by user, or left blank.
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * When True, Urn will be system allocated at begin assembly, When false, Urn must be provided by user if needed.
    */
    public autoAllocateUrnAtStart: boolean = false;
    /**
    * Cannot be unset once set.
    */
    public hasPrintedLabels: boolean = false;
    private _outputStillExpected: AssemblyOutput | null = null;
    public get outputStillExpected(): AssemblyOutput | null { return this._outputStillExpected }
    public set outputStillExpected(value: AssemblyOutput | null) {
      this._outputStillExpected = value === undefined ? null : value;
    }
    public readOnly: AssemblyHeaderReadOnly = new AssemblyHeaderReadOnly();
    public isHidden: boolean = false;
    public calculated: AssemblyHeaderCalculated = new AssemblyHeaderCalculated();
    private _plannedStartDate: LocalDate | null = null;
    /**
    * Date at which the assembly was planned to start.
    */
    public get plannedStartDate(): LocalDate | null { return this._plannedStartDate }
    public set plannedStartDate(value: LocalDate | null) {
      this._plannedStartDate = value === undefined ? null : value;
    }
    private _startDate: LocalDate | null = null;
    /**
    * Date at which the assembly was actually started - user editable (not captured by system). Not applicable to unstarted assemblies.
    */
    public get startDate(): LocalDate | null { return this._startDate }
    public set startDate(value: LocalDate | null) {
      this._startDate = value === undefined ? null : value;
    }
    /**
    * Vessel Fill Colour
    * Not Nullable
    */
    public vesselFillColour: string = "";
    private _currentProcessGroupId: Guid | null = null;
    /**
    * The ProcessGroup (if any) currently in progress.
    */
    public get currentProcessGroupId(): Guid | null { return this._currentProcessGroupId }
    public set currentProcessGroupId(value: Guid | null) {
      this._currentProcessGroupId = value === undefined ? null : value;
    }
    private _currentProcessGroupLikelyFinishDate: LocalDate | null = null;
    /**
    * The date on which the ProcessGroup (if any) currently in progress is likely to be completed. This represents the view of reality as estimated by the person performing the assembly.
    */
    public get currentProcessGroupLikelyFinishDate(): LocalDate | null { return this._currentProcessGroupLikelyFinishDate }
    public set currentProcessGroupLikelyFinishDate(value: LocalDate | null) {
      this._currentProcessGroupLikelyFinishDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.assemblyType = EnumAssemblyType.getByValue(jsonSerializedObject.assemblyType, this.assemblyType);
          this.bestBefore = jsonSerializedObject.bestBefore == null ? null : LocalDate.parse(jsonSerializedObject.bestBefore);
          this.currentExpectedCompletionDate = LocalDate.parse(jsonSerializedObject.currentExpectedCompletionDate);
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.recipeId = jsonSerializedObject.recipeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.urn = jsonSerializedObject.urn;
          this.autoAllocateUrnAtStart = jsonSerializedObject.autoAllocateUrnAtStart;
          this.hasPrintedLabels = jsonSerializedObject.hasPrintedLabels;
          this.outputStillExpected = jsonSerializedObject.outputStillExpected == null ? null : new AssemblyOutput(jsonSerializedObject.outputStillExpected);
          this.readOnly = new AssemblyHeaderReadOnly(jsonSerializedObject.readOnly);
          this.isHidden = jsonSerializedObject.isHidden;
          this.calculated = new AssemblyHeaderCalculated(jsonSerializedObject.calculated);
          this.plannedStartDate = jsonSerializedObject.plannedStartDate == null ? null : LocalDate.parse(jsonSerializedObject.plannedStartDate);
          this.startDate = jsonSerializedObject.startDate == null ? null : LocalDate.parse(jsonSerializedObject.startDate);
          this.vesselFillColour = jsonSerializedObject.vesselFillColour;
          this.currentProcessGroupId = jsonSerializedObject.currentProcessGroupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.currentProcessGroupId);
          this.currentProcessGroupLikelyFinishDate = jsonSerializedObject.currentProcessGroupLikelyFinishDate == null ? null : LocalDate.parse(jsonSerializedObject.currentProcessGroupLikelyFinishDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.assemblyType = this.assemblyType.value;
        toRet.bestBefore = this.bestBefore == null ? null : this.bestBefore.toString();
        toRet.currentExpectedCompletionDate = this.currentExpectedCompletionDate.toString();
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.recipeId = this.recipeId == null ? null : this.recipeId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.urn = this.urn;
        toRet.autoAllocateUrnAtStart = this.autoAllocateUrnAtStart;
        toRet.hasPrintedLabels = this.hasPrintedLabels;
        toRet.outputStillExpected = this.outputStillExpected == null ? null : this.outputStillExpected?.toJsonSerializedObject() ?? null;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.isHidden = this.isHidden;
        toRet.calculated = this.calculated.toJsonSerializedObject();
        toRet.plannedStartDate = this.plannedStartDate == null ? null : this.plannedStartDate.toString();
        toRet.startDate = this.startDate == null ? null : this.startDate.toString();
        toRet.vesselFillColour = this.vesselFillColour;
        toRet.currentProcessGroupId = this.currentProcessGroupId == null ? null : this.currentProcessGroupId.toString();
        toRet.currentProcessGroupLikelyFinishDate = this.currentProcessGroupLikelyFinishDate == null ? null : this.currentProcessGroupLikelyFinishDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyHeader.Id cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "recipeId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyHeader.RecipeId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "AssemblyHeader.StockLocationId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "outputStillExpected", rule: (v: any) => (v === null) || v.validate()},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
            {field: "currentProcessGroupId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyHeader.CurrentProcessGroupId cannot be empty"},
    ];
}


export class AssemblyIngredientLineReadOnly extends ModelObject<AssemblyIngredientLineReadOnly> {

    /**
    * Assembly Id
    */
    public assemblyId: Guid = Guid.createEmpty();
    private _quantityExpectedAtStart: number | null = null;
    /**
    * Quantity expected to be used as captured at the time the assembly was started (null if not started).
    */
    public get quantityExpectedAtStart(): number | null { return this._quantityExpectedAtStart }
    public set quantityExpectedAtStart(value: number | null) {
      this._quantityExpectedAtStart = value === undefined ? null : value;
    }
    private _totalCostExpectedAtStart: number | null = null;
    /**
    * Total cost of ingredient expected as captured at the time the assembly was started. (Quantity * StockItem Cost Price at time of start). (null if not started).
    */
    public get totalCostExpectedAtStart(): number | null { return this._totalCostExpectedAtStart }
    public set totalCostExpectedAtStart(value: number | null) {
      this._totalCostExpectedAtStart = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyId = Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.quantityExpectedAtStart = jsonSerializedObject.quantityExpectedAtStart == null ? null : jsonSerializedObject.quantityExpectedAtStart;
          this.totalCostExpectedAtStart = jsonSerializedObject.totalCostExpectedAtStart == null ? null : jsonSerializedObject.totalCostExpectedAtStart;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyId = this.assemblyId.toString();
        toRet.quantityExpectedAtStart = this.quantityExpectedAtStart == null ? null : this.quantityExpectedAtStart;
        toRet.totalCostExpectedAtStart = this.totalCostExpectedAtStart == null ? null : this.totalCostExpectedAtStart;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyId", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientLineReadOnly.AssemblyId cannot be empty"},
    ];
}


export class AssemblyIngredientLine extends ModelObject<AssemblyIngredientLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Quantity Still expected to be used.
    */
    public quantityStillExpected: number = 0;
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;
    /**
    * Stock Item Id Being Used.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Whether this ingredient is fixed to its current quantity regardless of adjustments to assembly output.If set to true, the ingredient required quantity will not be multiplied when adjusting planned assembly output quantities.
    */
    public fixedQuantity: boolean = false;
    /**
    * Notes.
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    public readOnly: AssemblyIngredientLineReadOnly = new AssemblyIngredientLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.quantityStillExpected = jsonSerializedObject.quantityStillExpected;
          this.sequence = jsonSerializedObject.sequence;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.fixedQuantity = jsonSerializedObject.fixedQuantity;
          this.notes = jsonSerializedObject.notes;
          this.readOnly = new AssemblyIngredientLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.quantityStillExpected = this.quantityStillExpected;
        toRet.sequence = this.sequence;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.fixedQuantity = this.fixedQuantity;
        toRet.notes = this.notes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientLine.Id cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientLine.UnitOfMeasureId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyIngredientUsedLineReadOnly extends ModelObject<AssemblyIngredientUsedLineReadOnly> {

    /**
    * Timestamp at which the ingredient used line was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Source Type this ingredient came from. : 0 = Stock, 1 = Assembly
    */
    public sourceType: EnumAssemblyIngredientSource = EnumAssemblyIngredientSource.stock;
    private _sourceAssemblyId: Guid | null = null;
    /**
    * Id of Source Assembly if this ingredient came from another assembly. Required if SourceType=Assembly.
    */
    public get sourceAssemblyId(): Guid | null { return this._sourceAssemblyId }
    public set sourceAssemblyId(value: Guid | null) {
      this._sourceAssemblyId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.sourceType = EnumAssemblyIngredientSource.getByValue(jsonSerializedObject.sourceType, this.sourceType);
          this.sourceAssemblyId = jsonSerializedObject.sourceAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sourceAssemblyId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.sourceType = this.sourceType.value;
        toRet.sourceAssemblyId = this.sourceAssemblyId == null ? null : this.sourceAssemblyId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sourceAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyIngredientUsedLineReadOnly.SourceAssemblyId cannot be empty"},
    ];
}


export class AssemblyIngredientUsedLine extends ModelObject<AssemblyIngredientUsedLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Assembly Ingredient Line Id for which this usage occurred.
    */
    public assemblyIngredientLineId: Guid = Guid.createEmpty();
    /**
    * Quantity Used
    */
    public quantityUsed: number = 0;
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Total cost of ingredients used in this line.
    */
    public totalCost: number = 0;
    /**
    * Stock batch/delivery tracking URN.
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    public readOnly: AssemblyIngredientUsedLineReadOnly = new AssemblyIngredientUsedLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.assemblyIngredientLineId = Guid.parseOrEmpty(jsonSerializedObject.assemblyIngredientLineId);
          this.quantityUsed = jsonSerializedObject.quantityUsed;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.totalCost = jsonSerializedObject.totalCost;
          this.urn = jsonSerializedObject.urn;
          this.readOnly = new AssemblyIngredientUsedLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.assemblyIngredientLineId = this.assemblyIngredientLineId.toString();
        toRet.quantityUsed = this.quantityUsed;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.totalCost = this.totalCost;
        toRet.urn = this.urn;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientUsedLine.Id cannot be empty"},
            {field: "assemblyIngredientLineId", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientUsedLine.AssemblyIngredientLineId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "AssemblyIngredientUsedLine.UnitOfMeasureId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyIngredientLineWithUsed extends ModelObject<AssemblyIngredientLineWithUsed> {

    public ingredient: AssemblyIngredientLine = new AssemblyIngredientLine();
    public ingredientUsedLines: AssemblyIngredientUsedLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ingredient = new AssemblyIngredientLine(jsonSerializedObject.ingredient);
          this.ingredientUsedLines = jsonSerializedObject.ingredientUsedLines.map((v: any) => new AssemblyIngredientUsedLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ingredient = this.ingredient.toJsonSerializedObject();
        toRet.ingredientUsedLines = this.ingredientUsedLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ingredient", rule: (v: any) => !!v || "ingredient is required"},
            {field: "ingredient", rule: (v: any) => v.validate()},
            {field: "ingredientUsedLines", rule: (v: any) => !!v || "ingredientUsedLines is required"},
            {field: "ingredientUsedLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyIngredientUsedLine[]).reduce((acc: Array<string>, curr: AssemblyIngredientUsedLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AssemblyItemProducedLineReadOnly extends ModelObject<AssemblyItemProducedLineReadOnly> {

    /**
    * Timestamp at which the item produced line was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Assembly Id
    */
    public assemblyId: Guid = Guid.createEmpty();
    /**
    * Target Type this output went to. : 0 = Stock, 1 = Assembly
    */
    public targetType: EnumAssemblyOutputTarget = EnumAssemblyOutputTarget.stock;
    private _targetAssemblyId: Guid | null = null;
    /**
    * Id of Target Assembly if this output went to another assembly. Required if TargetType=Assembly.
    */
    public get targetAssemblyId(): Guid | null { return this._targetAssemblyId }
    public set targetAssemblyId(value: Guid | null) {
      this._targetAssemblyId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.assemblyId = Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.targetType = EnumAssemblyOutputTarget.getByValue(jsonSerializedObject.targetType, this.targetType);
          this.targetAssemblyId = jsonSerializedObject.targetAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.targetAssemblyId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.assemblyId = this.assemblyId.toString();
        toRet.targetType = this.targetType.value;
        toRet.targetAssemblyId = this.targetAssemblyId == null ? null : this.targetAssemblyId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyId", rule: (v: any) => !v.isEmpty() || "AssemblyItemProducedLineReadOnly.AssemblyId cannot be empty"},
            {field: "targetAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyItemProducedLineReadOnly.TargetAssemblyId cannot be empty"},
    ];
}


export class AssemblyItemProducedLine extends ModelObject<AssemblyItemProducedLine> {

    /**
    * Stock item Id. All lines in one assembly must be for the same stock item.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Quantity Produced. A -ve value can act as a correction to a previous entry.
    */
    public quantityProduced: number = 0;
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    /**
    * Whether this output was a by-product of the assembly rather than the main output item.
    */
    public isByProduct: boolean = false;
    public readOnly: AssemblyItemProducedLineReadOnly = new AssemblyItemProducedLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.quantityProduced = jsonSerializedObject.quantityProduced;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.isByProduct = jsonSerializedObject.isByProduct;
          this.readOnly = new AssemblyItemProducedLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.quantityProduced = this.quantityProduced;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.isByProduct = this.isByProduct;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "AssemblyItemProducedLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "AssemblyItemProducedLine.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyCalculatedCosts extends ModelObject<AssemblyCalculatedCosts> {

    /**
    * Cost of ingredients still expected to be used. (Will be 0 if assembly is completed)
    */
    public costStillExpected: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.costStillExpected = jsonSerializedObject.costStillExpected;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.costStillExpected = this.costStillExpected;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class AssemblyProcessLineReadOnly extends ModelObject<AssemblyProcessLineReadOnly> {

    /**
    * Parent ProcessGroup.
    */
    public assemblyProcessGroupId: Guid = Guid.createEmpty();
    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the process line was completed on the system. Captured by system not user editable.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    private _completedByUserId: Guid | null = null;
    /**
    * User Id of the user who completed the process line. Captured by system not user editable.
    */
    public get completedByUserId(): Guid | null { return this._completedByUserId }
    public set completedByUserId(value: Guid | null) {
      this._completedByUserId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyProcessGroupId = Guid.parseOrEmpty(jsonSerializedObject.assemblyProcessGroupId);
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.completedByUserId = jsonSerializedObject.completedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.completedByUserId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyProcessGroupId = this.assemblyProcessGroupId.toString();
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.completedByUserId = this.completedByUserId == null ? null : this.completedByUserId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyProcessGroupId", rule: (v: any) => !v.isEmpty() || "AssemblyProcessLineReadOnly.AssemblyProcessGroupId cannot be empty"},
            {field: "completedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "AssemblyProcessLineReadOnly.CompletedByUserId cannot be empty"},
    ];
}


export class AssemblyProcessLine extends ModelObject<AssemblyProcessLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Is locked. Set to true if process came from recipe.
    */
    public isLocked: boolean = false;
    /**
    * Sequence
    */
    public sequence: number = 0;
    /**
    * Instruction
    * Not Nullable
    * Max Length : 100000
    */
    public instruction: string = "";
    /**
    * Instruction Notes
    * Not Nullable
    * Max Length : 100000
    */
    public instructionNotes: string = "";
    /**
    * Execution Notes
    * Not Nullable
    * Max Length : 100000
    */
    public executionNotes: string = "";
    /**
    * Complete
    */
    public complete: boolean = false;
    public readOnly: AssemblyProcessLineReadOnly = new AssemblyProcessLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isLocked = jsonSerializedObject.isLocked;
          this.sequence = jsonSerializedObject.sequence;
          this.instruction = jsonSerializedObject.instruction;
          this.instructionNotes = jsonSerializedObject.instructionNotes;
          this.executionNotes = jsonSerializedObject.executionNotes;
          this.complete = jsonSerializedObject.complete;
          this.readOnly = new AssemblyProcessLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.isLocked = this.isLocked;
        toRet.sequence = this.sequence;
        toRet.instruction = this.instruction;
        toRet.instructionNotes = this.instructionNotes;
        toRet.executionNotes = this.executionNotes;
        toRet.complete = this.complete;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyProcessLine.Id cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
            {field: "instruction", rule: (v: any) => v == null || v.length <= 100000 || "Instruction has too many characters (100000)"},
            {field: "instructionNotes", rule: (v: any) => v == null || v.length <= 100000 || "InstructionNotes has too many characters (100000)"},
            {field: "executionNotes", rule: (v: any) => v == null || v.length <= 100000 || "ExecutionNotes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyVesselRequirementReadOnly extends ModelObject<AssemblyVesselRequirementReadOnly> {

    /**
    * Parent ProcessGroup.
    */
    public assemblyProcessGroupId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyProcessGroupId = Guid.parseOrEmpty(jsonSerializedObject.assemblyProcessGroupId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyProcessGroupId = this.assemblyProcessGroupId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyProcessGroupId", rule: (v: any) => !v.isEmpty() || "AssemblyVesselRequirementReadOnly.AssemblyProcessGroupId cannot be empty"},
    ];
}


export class AssemblyVesselRequirement extends ModelObject<AssemblyVesselRequirement> {

    public id: Guid = Guid.createEmpty();
    /**
    * Required Vessel Capability.
    */
    public vesselCapabilityId: Guid = Guid.createEmpty();
    /**
    * Required minimum capacity of Vessel in Litres
    * Not Negative
    */
    public capacityLitres: number = 0;
    public readOnly: AssemblyVesselRequirementReadOnly = new AssemblyVesselRequirementReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.vesselCapabilityId = Guid.parseOrEmpty(jsonSerializedObject.vesselCapabilityId);
          this.capacityLitres = jsonSerializedObject.capacityLitres;
          this.readOnly = new AssemblyVesselRequirementReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.vesselCapabilityId = this.vesselCapabilityId.toString();
        toRet.capacityLitres = this.capacityLitres;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyVesselRequirement.Id cannot be empty"},
            {field: "vesselCapabilityId", rule: (v: any) => !v.isEmpty() || "AssemblyVesselRequirement.VesselCapabilityId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class AssemblyProcessGroupReadOnly extends ModelObject<AssemblyProcessGroupReadOnly> {

    /**
    * Assembly Id
    */
    public assemblyId: Guid = Guid.createEmpty();
    /**
    * Sequence (for ordering Lines).
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyId = Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyId = this.assemblyId.toString();
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyId", rule: (v: any) => !v.isEmpty() || "AssemblyProcessGroupReadOnly.AssemblyId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class AssemblyProcessGroup extends ModelObject<AssemblyProcessGroup> {

    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Duration in days this process group is planned to take.
    * Not Negative
    */
    public plannedDurationDays: number = 0;
    public readOnly: AssemblyProcessGroupReadOnly = new AssemblyProcessGroupReadOnly();
    public lines: AssemblyProcessLine[] = [];
    public vesselRequirements: AssemblyVesselRequirement[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.plannedDurationDays = jsonSerializedObject.plannedDurationDays;
          this.readOnly = new AssemblyProcessGroupReadOnly(jsonSerializedObject.readOnly);
          this.lines = jsonSerializedObject.lines.map((v: any) => new AssemblyProcessLine(v));
          this.vesselRequirements = jsonSerializedObject.vesselRequirements.map((v: any) => new AssemblyVesselRequirement(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.plannedDurationDays = this.plannedDurationDays;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        toRet.vesselRequirements = this.vesselRequirements.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "AssemblyProcessGroup.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "plannedDurationDays", rule: (v: any) => v == null || v >= 0 || "PlannedDurationDays is not greater than minimum allowed value: 0"},
            {field: "plannedDurationDays", rule: (v: any) => v == null || v <= 2147483647 || "PlannedDurationDays is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyProcessLine[]).reduce((acc: Array<string>, curr: AssemblyProcessLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "vesselRequirements", rule: (v: any) => !!v || "vesselRequirements is required"},
            {field: "vesselRequirements", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyVesselRequirement[]).reduce((acc: Array<string>, curr: AssemblyVesselRequirement) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class Assembly extends BaseModelObject<Assembly> {

    public header: AssemblyHeader = new AssemblyHeader();
    public ingredients: AssemblyIngredientLineWithUsed[] = [];
    public itemsProduced: AssemblyItemProducedLine[] = [];
    public calculatedCosts: AssemblyCalculatedCosts = new AssemblyCalculatedCosts();
    public processGroups: AssemblyProcessGroup[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new AssemblyHeader(jsonSerializedObject.header);
          this.ingredients = jsonSerializedObject.ingredients.map((v: any) => new AssemblyIngredientLineWithUsed(v));
          this.itemsProduced = jsonSerializedObject.itemsProduced.map((v: any) => new AssemblyItemProducedLine(v));
          this.calculatedCosts = new AssemblyCalculatedCosts(jsonSerializedObject.calculatedCosts);
          this.processGroups = jsonSerializedObject.processGroups.map((v: any) => new AssemblyProcessGroup(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.ingredients = this.ingredients.map(v => v.toJsonSerializedObject());
        toRet.itemsProduced = this.itemsProduced.map(v => v.toJsonSerializedObject());
        toRet.calculatedCosts = this.calculatedCosts.toJsonSerializedObject();
        toRet.processGroups = this.processGroups.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "ingredients", rule: (v: any) => !!v || "ingredients is required"},
            {field: "ingredients", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyIngredientLineWithUsed[]).reduce((acc: Array<string>, curr: AssemblyIngredientLineWithUsed) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "itemsProduced", rule: (v: any) => !!v || "itemsProduced is required"},
            {field: "itemsProduced", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyItemProducedLine[]).reduce((acc: Array<string>, curr: AssemblyItemProducedLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "calculatedCosts", rule: (v: any) => !!v || "calculatedCosts is required"},
            {field: "calculatedCosts", rule: (v: any) => v.validate()},
            {field: "processGroups", rule: (v: any) => !!v || "processGroups is required"},
            {field: "processGroups", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyProcessGroup[]).reduce((acc: Array<string>, curr: AssemblyProcessGroup) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class IngredientUsedModification extends ModelObject<IngredientUsedModification> {

    public id: Guid = Guid.createEmpty();
    /**
    * Total cost of ingredients used in this line.
    */
    public totalCost: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.totalCost = jsonSerializedObject.totalCost;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.totalCost = this.totalCost;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "IngredientUsedModification.Id cannot be empty"},
    ];
}


export class AssemblySummary extends ModelObject<AssemblySummary> {

    public header: AssemblyHeader = new AssemblyHeader();
    public calculatedCosts: AssemblyCalculatedCosts = new AssemblyCalculatedCosts();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new AssemblyHeader(jsonSerializedObject.header);
          this.calculatedCosts = new AssemblyCalculatedCosts(jsonSerializedObject.calculatedCosts);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.calculatedCosts = this.calculatedCosts.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "calculatedCosts", rule: (v: any) => !!v || "calculatedCosts is required"},
            {field: "calculatedCosts", rule: (v: any) => v.validate()},
    ];
}


export class AssemblyFilter extends ModelObject<AssemblyFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _statuses: EnumAssemblyStatus[] | null = null;
    /**
    * If provided only matching statuses are returned
    */
    public get statuses(): EnumAssemblyStatus[] | null { return this._statuses }
    public set statuses(value: EnumAssemblyStatus[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _assemblyTypes: EnumAssemblyType[] | null = null;
    /**
    * If provided only matching types are returned
    */
    public get assemblyTypes(): EnumAssemblyType[] | null { return this._assemblyTypes }
    public set assemblyTypes(value: EnumAssemblyType[] | null) {
      this._assemblyTypes = value === undefined ? null : value;
    }
    private _fromTargetDateInclusive: LocalDate | null = null;
    /**
    * Minimum Due Date
    */
    public get fromTargetDateInclusive(): LocalDate | null { return this._fromTargetDateInclusive }
    public set fromTargetDateInclusive(value: LocalDate | null) {
      this._fromTargetDateInclusive = value === undefined ? null : value;
    }
    private _toTargetDateInclusive: LocalDate | null = null;
    /**
    * Maximum Due Date
    */
    public get toTargetDateInclusive(): LocalDate | null { return this._toTargetDateInclusive }
    public set toTargetDateInclusive(value: LocalDate | null) {
      this._toTargetDateInclusive = value === undefined ? null : value;
    }
    private _fromPlannedStartDateInclusive: LocalDate | null = null;
    /**
    * Minimum Planned Start Date
    */
    public get fromPlannedStartDateInclusive(): LocalDate | null { return this._fromPlannedStartDateInclusive }
    public set fromPlannedStartDateInclusive(value: LocalDate | null) {
      this._fromPlannedStartDateInclusive = value === undefined ? null : value;
    }
    private _toPlannedStartDateInclusive: LocalDate | null = null;
    /**
    * Maximum Planned Start Date
    */
    public get toPlannedStartDateInclusive(): LocalDate | null { return this._toPlannedStartDateInclusive }
    public set toPlannedStartDateInclusive(value: LocalDate | null) {
      this._toPlannedStartDateInclusive = value === undefined ? null : value;
    }
    private _stockLocations: Guid[] | null = null;
    public get stockLocations(): Guid[] | null { return this._stockLocations }
    public set stockLocations(value: Guid[] | null) {
      this._stockLocations = value === undefined ? null : value;
    }
    /**
    * Yes = Hidden Only, No = Not Hidden Only, Either = Either
    */
    public hiddenOrNot: EnumYesNoEither = EnumYesNoEither.either;
    private _fromCompletedAt: Instant | null = null;
    public get fromCompletedAt(): Instant | null { return this._fromCompletedAt }
    public set fromCompletedAt(value: Instant | null) {
      this._fromCompletedAt = value === undefined ? null : value;
    }
    private _toCompletedAt: Instant | null = null;
    public get toCompletedAt(): Instant | null { return this._toCompletedAt }
    public set toCompletedAt(value: Instant | null) {
      this._toCompletedAt = value === undefined ? null : value;
    }
    private _fromStartedAt: Instant | null = null;
    public get fromStartedAt(): Instant | null { return this._fromStartedAt }
    public set fromStartedAt(value: Instant | null) {
      this._fromStartedAt = value === undefined ? null : value;
    }
    private _toStartedAt: Instant | null = null;
    public get toStartedAt(): Instant | null { return this._toStartedAt }
    public set toStartedAt(value: Instant | null) {
      this._toStartedAt = value === undefined ? null : value;
    }
    private _hasOutput: boolean | null = null;
    public get hasOutput(): boolean | null { return this._hasOutput }
    public set hasOutput(value: boolean | null) {
      this._hasOutput = value === undefined ? null : value;
    }
    private _hasUrn: boolean | null = null;
    public get hasUrn(): boolean | null { return this._hasUrn }
    public set hasUrn(value: boolean | null) {
      this._hasUrn = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumAssemblyStatus.getByValue(v));
          this.assemblyTypes = jsonSerializedObject.assemblyTypes == null ? null : jsonSerializedObject.assemblyTypes.map((v: any) => EnumAssemblyType.getByValue(v));
          this.fromTargetDateInclusive = jsonSerializedObject.fromTargetDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromTargetDateInclusive);
          this.toTargetDateInclusive = jsonSerializedObject.toTargetDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toTargetDateInclusive);
          this.fromPlannedStartDateInclusive = jsonSerializedObject.fromPlannedStartDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromPlannedStartDateInclusive);
          this.toPlannedStartDateInclusive = jsonSerializedObject.toPlannedStartDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toPlannedStartDateInclusive);
          this.stockLocations = jsonSerializedObject.stockLocations == null ? null : jsonSerializedObject.stockLocations.map((v: any) => Guid.parseOrEmpty(v));
          this.hiddenOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.hiddenOrNot, this.hiddenOrNot);
          this.fromCompletedAt = jsonSerializedObject.fromCompletedAt == null ? null : Instant.parse(jsonSerializedObject.fromCompletedAt);
          this.toCompletedAt = jsonSerializedObject.toCompletedAt == null ? null : Instant.parse(jsonSerializedObject.toCompletedAt);
          this.fromStartedAt = jsonSerializedObject.fromStartedAt == null ? null : Instant.parse(jsonSerializedObject.fromStartedAt);
          this.toStartedAt = jsonSerializedObject.toStartedAt == null ? null : Instant.parse(jsonSerializedObject.toStartedAt);
          this.hasOutput = jsonSerializedObject.hasOutput == null ? null : jsonSerializedObject.hasOutput;
          this.hasUrn = jsonSerializedObject.hasUrn == null ? null : jsonSerializedObject.hasUrn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.assemblyTypes = this.assemblyTypes == null ? null : this.assemblyTypes.map(v => v.value);
        toRet.fromTargetDateInclusive = this.fromTargetDateInclusive == null ? null : this.fromTargetDateInclusive.toString();
        toRet.toTargetDateInclusive = this.toTargetDateInclusive == null ? null : this.toTargetDateInclusive.toString();
        toRet.fromPlannedStartDateInclusive = this.fromPlannedStartDateInclusive == null ? null : this.fromPlannedStartDateInclusive.toString();
        toRet.toPlannedStartDateInclusive = this.toPlannedStartDateInclusive == null ? null : this.toPlannedStartDateInclusive.toString();
        toRet.stockLocations = this.stockLocations == null ? null : this.stockLocations.map(v => v.toString());
        toRet.hiddenOrNot = this.hiddenOrNot.value;
        toRet.fromCompletedAt = this.fromCompletedAt == null ? null : this.fromCompletedAt.toString();
        toRet.toCompletedAt = this.toCompletedAt == null ? null : this.toCompletedAt.toString();
        toRet.fromStartedAt = this.fromStartedAt == null ? null : this.fromStartedAt.toString();
        toRet.toStartedAt = this.toStartedAt == null ? null : this.toStartedAt.toString();
        toRet.hasOutput = this.hasOutput == null ? null : this.hasOutput;
        toRet.hasUrn = this.hasUrn == null ? null : this.hasUrn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredAssemblySummaries extends ModelObject<FilteredAssemblySummaries> {

    public matchingCount: number = 0;
    public assemblySummaries: AssemblySummary[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.assemblySummaries = jsonSerializedObject.assemblySummaries.map((v: any) => new AssemblySummary(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.assemblySummaries = this.assemblySummaries.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "assemblySummaries", rule: (v: any) => !!v || "assemblySummaries is required"},
            {field: "assemblySummaries", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblySummary[]).reduce((acc: Array<string>, curr: AssemblySummary) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AssemblyStockTimeRange extends ModelObject<AssemblyStockTimeRange> {

    public includeIngredientsUsedFromStock: boolean = false;
    public includeOutputsToStock: boolean = false;
    private _fromInstant: Instant | null = null;
    public get fromInstant(): Instant | null { return this._fromInstant }
    public set fromInstant(value: Instant | null) {
      this._fromInstant = value === undefined ? null : value;
    }
    private _beforeInstant: Instant | null = null;
    public get beforeInstant(): Instant | null { return this._beforeInstant }
    public set beforeInstant(value: Instant | null) {
      this._beforeInstant = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.includeIngredientsUsedFromStock = jsonSerializedObject.includeIngredientsUsedFromStock;
          this.includeOutputsToStock = jsonSerializedObject.includeOutputsToStock;
          this.fromInstant = jsonSerializedObject.fromInstant == null ? null : Instant.parse(jsonSerializedObject.fromInstant);
          this.beforeInstant = jsonSerializedObject.beforeInstant == null ? null : Instant.parse(jsonSerializedObject.beforeInstant);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.includeIngredientsUsedFromStock = this.includeIngredientsUsedFromStock;
        toRet.includeOutputsToStock = this.includeOutputsToStock;
        toRet.fromInstant = this.fromInstant == null ? null : this.fromInstant.toString();
        toRet.beforeInstant = this.beforeInstant == null ? null : this.beforeInstant.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class FilteredAssemblyHeaders extends ModelObject<FilteredAssemblyHeaders> {

    public matchingCount: number = 0;
    public assemblyHeaders: AssemblyHeader[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.assemblyHeaders = jsonSerializedObject.assemblyHeaders.map((v: any) => new AssemblyHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.assemblyHeaders = this.assemblyHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "assemblyHeaders", rule: (v: any) => !!v || "assemblyHeaders is required"},
            {field: "assemblyHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AssemblyHeader[]).reduce((acc: Array<string>, curr: AssemblyHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class AssembledBatchDetails extends ModelObject<AssembledBatchDetails> {

    public stockItemId: Guid = Guid.createEmpty();
    public urn: string = "";
    public oldestAssembledDate: LocalDate = LocalDate.ofEpochDay(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
          this.oldestAssembledDate = LocalDate.parse(jsonSerializedObject.oldestAssembledDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        toRet.oldestAssembledDate = this.oldestAssembledDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "AssembledBatchDetails.StockItemId cannot be empty"},
    ];
}


export class AssemblyStockExpectationLine extends ModelObject<AssemblyStockExpectationLine> {

    public assemblyId: Guid = Guid.createEmpty();
    public status: EnumAssemblyStatus = EnumAssemblyStatus.open;
    public expectedCompletionDate: LocalDate = LocalDate.ofEpochDay(0);
    public stockLocationId: Guid = Guid.createEmpty();
    /**
    * Expected stock change. +ve means stock will increase.
    */
    public signedQuantity: number = 0;
    /**
    * Identifies if this is an expected ingredient (opposed to expected output). Explicit as sign of SignedQuantity may not correlate.
    */
    public isIngredient: boolean = false;
    public stockItemId: Guid = Guid.createEmpty();
    public unitOfMeasureId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyId = Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.status = EnumAssemblyStatus.getByValue(jsonSerializedObject.status, this.status);
          this.expectedCompletionDate = LocalDate.parse(jsonSerializedObject.expectedCompletionDate);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.signedQuantity = jsonSerializedObject.signedQuantity;
          this.isIngredient = jsonSerializedObject.isIngredient;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyId = this.assemblyId.toString();
        toRet.status = this.status.value;
        toRet.expectedCompletionDate = this.expectedCompletionDate.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.signedQuantity = this.signedQuantity;
        toRet.isIngredient = this.isIngredient;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyId", rule: (v: any) => !v.isEmpty() || "AssemblyStockExpectationLine.AssemblyId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "AssemblyStockExpectationLine.StockLocationId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "AssemblyStockExpectationLine.StockItemId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "AssemblyStockExpectationLine.UnitOfMeasureId cannot be empty"},
    ];
}


export class ProcessLineForAssembly extends ModelObject<ProcessLineForAssembly> {

    public assemblyId: Guid = Guid.createEmpty();
    public processLine: AssemblyProcessLine = new AssemblyProcessLine();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyId = Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.processLine = new AssemblyProcessLine(jsonSerializedObject.processLine);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyId = this.assemblyId.toString();
        toRet.processLine = this.processLine.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyId", rule: (v: any) => !v.isEmpty() || "ProcessLineForAssembly.AssemblyId cannot be empty"},
            {field: "processLine", rule: (v: any) => !!v || "processLine is required"},
            {field: "processLine", rule: (v: any) => v.validate()},
    ];
}


export class AssemblyLiquidEvaluation extends ModelObject<AssemblyLiquidEvaluation> {

    public assemblyId: Guid = Guid.createEmpty();
    public stockLocationId: Guid = Guid.createEmpty();
    public heldAtStartOfPeriodLitres: number = 0;
    public heldAtEndOfPeriodLitres: number = 0;
    public producedLitres: number = 0;
    public reprocessedLitres: number = 0;
    public discardedLitres: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.assemblyId = Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.heldAtStartOfPeriodLitres = jsonSerializedObject.heldAtStartOfPeriodLitres;
          this.heldAtEndOfPeriodLitres = jsonSerializedObject.heldAtEndOfPeriodLitres;
          this.producedLitres = jsonSerializedObject.producedLitres;
          this.reprocessedLitres = jsonSerializedObject.reprocessedLitres;
          this.discardedLitres = jsonSerializedObject.discardedLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.assemblyId = this.assemblyId.toString();
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.heldAtStartOfPeriodLitres = this.heldAtStartOfPeriodLitres;
        toRet.heldAtEndOfPeriodLitres = this.heldAtEndOfPeriodLitres;
        toRet.producedLitres = this.producedLitres;
        toRet.reprocessedLitres = this.reprocessedLitres;
        toRet.discardedLitres = this.discardedLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "assemblyId", rule: (v: any) => !v.isEmpty() || "AssemblyLiquidEvaluation.AssemblyId cannot be empty"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "AssemblyLiquidEvaluation.StockLocationId cannot be empty"},
    ];
}


export class AssemblyService extends ServiceBase {

  public async getAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Assembly> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => new Assembly(response.assembly);;
    return this.makeJsonRequest("assembly/v1/getAssembly", req, false, extractor, opts);
  };

  public async getAssemblies(tenantId: Guid, assemblyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Assembly[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyIds:assemblyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblies.map((v: any) => new Assembly(v));;
    return this.makeJsonRequest("assembly/v1/getAssemblies", req, false, extractor, opts);
  };

  public async savePlannedAssembly(tenantId: Guid, header: AssemblyHeader, ingredients: readonly AssemblyIngredientLine[], processGroups: readonly AssemblyProcessGroup[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Assembly> {
    const req = {
    tenantId:tenantId.toString(),
    header:header.toJsonSerializedObject(),
    ingredients:ingredients.map(v => v.toJsonSerializedObject()),
    processGroups:processGroups.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => new Assembly(response.assembly);;
    return this.makeJsonRequest("assembly/v1/savePlannedAssembly", req, true, extractor, opts);
  };

  public async setAssemblyIsHidden(tenantId: Guid, assemblyId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/setAssemblyIsHidden", req, true, extractor, opts);
  };

  public async cancelAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/cancelAssembly", req, true, extractor, opts);
  };

  public async beginAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Assembly> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => new Assembly(response.assembly);;
    return this.makeJsonRequest("assembly/v1/beginAssembly", req, true, extractor, opts);
  };

  public async saveAssemblyProgress(tenantId: Guid, assemblyHeader: AssemblyHeader, ingredients: readonly AssemblyIngredientLine[], ingredientsToAddToUsedList: readonly AssemblyIngredientUsedLine[], processGroups: readonly AssemblyProcessGroup[], complete: boolean, ingredientUsedModifications: readonly IngredientUsedModification[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyHeader:assemblyHeader.toJsonSerializedObject(),
    ingredients:ingredients.map(v => v.toJsonSerializedObject()),
    ingredientsToAddToUsedList:ingredientsToAddToUsedList.map(v => v.toJsonSerializedObject()),
    processGroups:processGroups.map(v => v.toJsonSerializedObject()),
    complete:complete,
    ingredientUsedModifications:ingredientUsedModifications.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/saveAssemblyProgress", req, true, extractor, opts);
  };

  public async uncompleteAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/uncompleteAssembly", req, true, extractor, opts);
  };

  public async getAllAssemblySummaries(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblySummary[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.assemblySummaries.map((v: any) => new AssemblySummary(v));;
    return this.makeJsonRequest("assembly/v1/getAllAssemblySummaries", req, false, extractor, opts);
  };

  public async getFilteredAssemblySummaries(tenantId: Guid, filter: AssemblyFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredAssemblySummaries> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredAssemblySummaries(response.results);;
    return this.makeJsonRequest("assembly/v1/getFilteredAssemblySummaries", req, false, extractor, opts);
  };

  public async getAssembledBatchDetails(tenantId: Guid, stockItemIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssembledBatchDetails[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockItemIds:stockItemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assembledBatchDetails.map((v: any) => new AssembledBatchDetails(v));;
    return this.makeJsonRequest("assembly/v1/getAssembledBatchDetails", req, false, extractor, opts);
  };

  public async addOutputToAnotherAssembly(tenantId: Guid, sourceAssemblyId: Guid, targetAssemblyId: Guid, stockItemId: Guid, quantity: number, stockItemCostPrice: number, unitOfMeasureId: Guid, adjustSourceExpectedOutput: boolean, adjustTargetExpectedIngredients: boolean, isByProduct: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    sourceAssemblyId:sourceAssemblyId.toString(),
    targetAssemblyId:targetAssemblyId.toString(),
    stockItemId:stockItemId.toString(),
    quantity:quantity,
    stockItemCostPrice:stockItemCostPrice,
    unitOfMeasureId:unitOfMeasureId.toString(),
    adjustSourceExpectedOutput:adjustSourceExpectedOutput,
    adjustTargetExpectedIngredients:adjustTargetExpectedIngredients,
    isByProduct:isByProduct,
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/addOutputToAnotherAssembly", req, true, extractor, opts);
  };

  public async getAssemblyStockExpectations(tenantId: Guid, limitStockLocationIds: readonly Guid[] | null, limitStockItemIds: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyStockExpectationLine[]> {
    const req = {
    tenantId:tenantId.toString(),
    limitStockLocationIds:limitStockLocationIds == null ? null : limitStockLocationIds.map(v => v.toString()),
    limitStockItemIds:limitStockItemIds == null ? null : limitStockItemIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new AssemblyStockExpectationLine(v));;
    return this.makeJsonRequest("assembly/v1/getAssemblyStockExpectations", req, false, extractor, opts);
  };

  public async getAssemblyHeadersByIds(tenantId: Guid, assemblyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyHeader[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyIds:assemblyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.assemblyHeaders.map((v: any) => new AssemblyHeader(v));;
    return this.makeJsonRequest("assembly/v1/getAssemblyHeadersByIds", req, false, extractor, opts);
  };

  public async getAllAssemblyProcessLines(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessLineForAssembly[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.processes.map((v: any) => new ProcessLineForAssembly(v));;
    return this.makeJsonRequest("assembly/v1/getAllAssemblyProcessLines", req, false, extractor, opts);
  };

  public async getAssemblyProcessLines(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProcessLineForAssembly[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.processes.map((v: any) => new ProcessLineForAssembly(v));;
    return this.makeJsonRequest("assembly/v1/getAssemblyProcessLines", req, false, extractor, opts);
  };

  public async getAssemblyIngredientsStillExpected(tenantId: Guid, assemblyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyIngredientLine[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyIds:assemblyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.ingredients.map((v: any) => new AssemblyIngredientLine(v));;
    return this.makeJsonRequest("assembly/v1/getAssemblyIngredientsStillExpected", req, false, extractor, opts);
  };

  public async savePrintUpdatesForAssembly(tenantId: Guid, assemblyId: Guid, hasPrintedLabels: boolean, bestBeforeDate: LocalDate | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    hasPrintedLabels:hasPrintedLabels,
    bestBeforeDate:bestBeforeDate == null ? null : bestBeforeDate.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/savePrintUpdatesForAssembly", req, true, extractor, opts);
  };

  public async evaluateLiquidProduction(tenantId: Guid, fromDate: LocalDate, toDate: LocalDate, alcoholTypes: readonly EnumAlcoholType[], stockLocations: readonly Guid[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AssemblyLiquidEvaluation[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDate:fromDate.toString(),
    toDate:toDate.toString(),
    alcoholTypes:alcoholTypes.map(v => v.value),
    stockLocations:stockLocations == null ? null : stockLocations.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new AssemblyLiquidEvaluation(v));;
    return this.makeJsonRequest("assembly/v1/evaluateLiquidProduction", req, false, extractor, opts);
  };

  public async addOutputToAssembly(tenantId: Guid, assemblyHeader: AssemblyHeader, itemsToAddToProducedList: readonly AssemblyItemProducedLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyHeader:assemblyHeader.toJsonSerializedObject(),
    itemsToAddToProducedList:itemsToAddToProducedList.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("assembly/v1/addOutputToAssembly", req, true, extractor, opts);
  };

  public async getAllAssemblies(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Assembly[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.assemblies.map((v: any) => new Assembly(v));;
    return this.makeJsonRequest("assembly/v1/getAllAssemblies", req, false, extractor, opts);
  };
}

export const assemblyService = new AssemblyService();